import { UserAttributes } from '@/types/UserAttributes';
import { useAppStore } from '@/store/app';
import { ClinicAttributes } from '@/types/ClinicAttributes';
import { api } from '@/helpers/api';
import { AuthResponse } from '@/types/responses/AuthResponse';
import { UserRoles } from '@/types/enums/UserRoles';
import { ResourceResponse } from '@/types/responses/ResourceResponse';

let refreshTokenInterval: ReturnType<typeof setInterval> | null = null;

export function isAuthenticated() : boolean {
    const appStore = useAppStore();
    return !!appStore.userAuthToken;
}

export function loadUser() : void {
    if (!localStorage.getItem('userToken') || !localStorage.getItem('user')) {
        return;
    }

    try {
        const userAuthToken = localStorage.getItem('userToken') as string;
        const user = JSON.parse(localStorage.getItem('user') as string) as unknown as UserAttributes;
        const activeClinicData = JSON.parse(localStorage.getItem('activeClinic') as string) as unknown as ClinicAttributes;

        const appStore = useAppStore();
        appStore.update({
            user,
            userAuthToken,
            activeClinic: activeClinicData
        });

        if (refreshTokenInterval) {
            clearInterval(refreshTokenInterval);
        }
        refreshTokenInterval = setInterval(async () => refreshToken(), 5 * 60 * 1000);
    } catch (error) {
        console.error('Unable to load user');
    }
}

export function authenticate(userObj: UserAttributes, token: string) : void {
    const appStore = useAppStore();
    appStore.update({
        user: userObj,
        userAuthToken: token
    });

    localStorage.setItem('userToken', token);
    localStorage.setItem('user', JSON.stringify(userObj));

    if (userObj.clinics && userObj.clinics.length > 0) {
        let activeClinicData = null;

        if (localStorage.getItem('activeClinic')) {
            try {
                activeClinicData = JSON.parse(localStorage.getItem('activeClinic') as string) as ClinicAttributes;
            } catch (error) {
                activeClinicData = userObj.clinics[0];
            }
        } else {
            activeClinicData = userObj.clinics[0];
        }

        localStorage.setItem('activeClinic', JSON.stringify(activeClinicData));
        appStore.update({
            activeClinic: activeClinicData
        });
    }

    if (refreshTokenInterval) {
        clearInterval(refreshTokenInterval);
    }
    setInterval(async () => refreshToken(), 5 * 60 * 1000);
}

export async function refreshToken() {
    const result = await api<AuthResponse>({
        endpoint: 'refresh',
        method: 'post'
    });

    if (result.status !== 200) {
        return;
    }

    const token = result.data.access_token;
    const appStore = useAppStore();
    appStore.update({
        userAuthToken: token
    });

    localStorage.setItem('userToken', token);
}

export function authenticateWithImpersonationToken(token: string, userObj: UserAttributes) : void {
    localStorage.removeItem('activeClinic');
    authenticate(userObj, token);
}

export function logout() : void {
    localStorage.removeItem('userToken');
    localStorage.removeItem('user');
    localStorage.removeItem('activeClinic');
}

export function user() : UserAttributes {
    const appStore = useAppStore();
    if (!appStore.user) {
        throw new Error('User not logged in');
    }

    return appStore.user;
}

export const getUser = user;

export async function refreshUser() {
    const response = await api<ResourceResponse<UserAttributes>>({
        endpoint: 'user-profile'
    });

    if (response.status !== 200) {
        throw new Error('Can\'t update user info');
    }

    const userObj = response.data.data;
    const appStore = useAppStore();

    appStore.update({
        user: userObj
    });
    localStorage.setItem('user', JSON.stringify(userObj));

    let clinic = null;

    if (activeClinic()) {
        clinic = userObj.clinics?.filter(c => c.id === activeClinic().id)[0];
    } else {
        clinic = userObj.clinics ? userObj.clinics[0] : null;
    }

    localStorage.setItem('activeClinic', JSON.stringify(clinic));
    appStore.update({
        activeClinic: clinic
    });
}

export function accessToken() : string {
    const appStore = useAppStore();
    if (!appStore.userAuthToken) {
        throw new Error('User not logged in');
    }

    return appStore.userAuthToken;
}

export function activeClinic() : ClinicAttributes {
    const appStore = useAppStore();
    return appStore.activeClinic;
}

export function switchActiveClinic(clinic: ClinicAttributes) : void {
    const appStore = useAppStore();
    appStore.update({
        activeClinic: clinic
    });

    localStorage.setItem('activeClinic', JSON.stringify(clinic));
}

export function userRoles() : number {
    const appStore = useAppStore();
    const roles = appStore.activeClinic?.roles;

    if (!roles) {
        return 0;
    }

    return roles.filter((role: {user_id: string, roles: number}) => role.user_id === user().id)[0]?.roles ?? 0;
}

const allowedRoleActions : Record<number, string[]> = {
    [UserRoles.Owner]: [
        'dashboard',
        'settings',
        'services'
    ],
    [UserRoles.Admin]: [
        'dashboard',
        'services',
        'patients'
    ],
    [UserRoles.Doctor]: [
        'dashboard',
        'patients'
    ]
};

// Vue.prototype.$can = (action: any) => {
//     if (!action) {
//         return true;
//     }
//
//     const userRole = userRoles();
//
//     for (const role in allowedRoleActions) {
//         if (!(userRole & (role as unknown as number))) {
//             continue;
//         }
//
//         if (allowedRoleActions[role].includes(action)) {
//             return true;
//         }
//     }
//
//     return false;
// };
